/**
 * Helpers shared by the Settings "managed list" pages (expense categories,
 * payment methods) for **system** rows: rows whose immutable ``code`` is
 * the real identity that code-side logic pins to, and whose display
 * ``name`` operators may rename freely.
 *
 * When a system row is renamed away from its canonical name we surface the
 * fixed code in brackets (e.g. "Kasse (cash_drawer)") so the original
 * intent stays visible. Detection is "does the current name still slugify
 * to the code?" — every canonical name slugifies exactly to its code, so
 * an untouched system row reads as unchanged and shows nothing extra.
 */

/** Minimal shape: a system-flagged row with a renameable name + fixed code. */
export interface SystemLabelRow {
  name: string;
  code: string;
  system_seeded: boolean;
}

/**
 * Slugify a display name the same way the backend does
 * (``_slug_from_name`` in ``routes/expense_categories.py`` /
 * ``_slugify_code`` in ``routes/payment_methods.py``): lowercase,
 * non-alphanumerics collapsed to a single underscore, edges trimmed.
 */
export const slugifyCode = (name: string): string => {
  const out: string[] = [];
  let prevUnderscore = false;
  for (const ch of name.toLowerCase()) {
    if (/[a-z0-9]/.test(ch)) {
      out.push(ch);
      prevUnderscore = false;
    } else if (!prevUnderscore) {
      out.push("_");
      prevUnderscore = true;
    }
  }
  return out.join("").replace(/^_+|_+$/g, "");
};

/** True when a system row's display name has been renamed away from its
 *  canonical (code-matching) name — the trigger for the bracketed code. */
export const isRenamedSystemRow = (r: SystemLabelRow): boolean =>
  r.system_seeded && slugifyCode(r.name) !== r.code;

/** Table label: the fixed code in brackets once a system row is renamed
 *  (e.g. "Kasse (cash_drawer)"); otherwise just the name. */
export const systemRowLabel = (r: SystemLabelRow): string =>
  isRenamedSystemRow(r) ? `${r.name} (${r.code})` : r.name;
